/**
 * Pack loader (II.D.2 / #57; identity-v2 A4 / #174).
 *
 * Single-pack load is the Walking Skeleton primitive. II.D.2 extends
 * the loader to compose multiple packs in deterministic dependency
 * order (`03a-entities.md §11.4`):
 *
 *   1. The dependency DAG is topologically sorted; cycles are
 *      rejected at load time with a structured error naming the cycle.
 *   2. Predicate ids are **opaque, minted, immutable** (`pred_<uuidv4>`,
 *      ADR-0004 §1). Two packs can therefore never declare the same id —
 *      collisions are structurally impossible, so the old load-order
 *      `{packId}.{id}` namespacing is gone (ADR-0004 Consequences, #174).
 *      Two packs *may* declare predicates with the same display NAME; both
 *      keep their distinct ids and both land in the registry. A
 *      non-blocking `predicate_name_duplicated` advisory records the
 *      shared name so a UI can disambiguate by pack origin / value-kind.
 *   3. `previous_names` is name-history only (ADR-0004 §1 / #172) — it no
 *      longer resolves identity. Ops in the log carry an opaque id that
 *      never changes; there is nothing to alias at read time. The op log
 *      is NEVER rewritten — the replay invariant (02-architecture.md
 *      §4.2) holds.
 *
 * Bundled vs user-authored packs: Phase 1 only ships engine-bundled
 * JSON packs (`core`, `generic-medieval-fantasy`). The `.pack` zip
 * distribution and asset bundling live in Phase 2.
 */
import { registerBuiltins } from './builtins.js';
import { PackSchema, type Pack, isPackVersionCompatible } from './pack.js';
import genericMedievalFantasyJson from './packs/generic-medieval-fantasy.json' with { type: 'json' };
import officialCorePackJson from './packs/official-core.json' with { type: 'json' };
import { defaultLocaleName, PredicateRegistry } from './predicate.js';

/**
 * Engine-bundled, subscribable packs (ADR-0004 §3, #183).
 *
 * `core.json` is GONE: ADR-0004 killed the privileged bundled-content pack.
 * The former core.json vocabulary (`population`, `inhabited`, the calendars, …)
 * is reborn as `official-core` — the **first official pack**, carrying opaque
 * minted ids (`pred_/trait_/enm_`) + mandatory localized names, produced through
 * the same extraction pipeline a third-party author uses (no privileged path).
 * It is a normal *subscribable* bundled pack — a brand-new campaign is NOT
 * empty (it always has the built-in `name`, ADR-0004 §2), but it does NOT carry
 * the official content vocabulary until it explicitly subscribes `official-core`.
 *
 * `generic-medieval-fantasy` is the demo pack (II.D.1 / #58).
 */
const BUNDLED_PACKS: Record<string, unknown> = {
  'official-core': officialCorePackJson,
  'generic-medieval-fantasy': genericMedievalFantasyJson,
};

/** The id of the official starter pack (ADR-0004 §3, #183). */
export const OFFICIAL_CORE_PACK_ID = 'official-core';

export function listBundledPacks(): readonly string[] {
  return Object.keys(BUNDLED_PACKS);
}

export function loadBundledPack(packId: string): Pack {
  const raw = BUNDLED_PACKS[packId];
  if (!raw) throw new Error(`unknown bundled pack: ${packId}`);
  const pack = PackSchema.parse(raw);
  if (!isPackVersionCompatible(pack.manifest.id, pack.manifest.version)) {
    throw new Error(
      `pack ${pack.manifest.id}@${pack.manifest.version} is not compatible with this engine`,
    );
  }
  return pack;
}

/**
 * Register a pack's predicates with a registry. Idempotent — adding
 * the same pack twice yields the same registry state (ids are opaque +
 * stable, so re-adding overwrites the same key with an identical value).
 *
 * No collision handling: opaque ids never collide (ADR-0004 §1, #174).
 */
export function registerPack(registry: PredicateRegistry, pack: Pack): void {
  for (const declaration of pack.predicates) {
    registry.add(declaration);
  }
}

/**
 * Bootstrap a registry with the **built-in system predicates** only
 * (ADR-0004 §2/§3, #183) — chiefly `name`, present in EVERY campaign
 * independent of any pack subscription.
 *
 * core.json is gone (#183): there is no privileged bundled content vocabulary
 * to load here. A campaign that wants the former core content
 * (`population`, `inhabited`, …) subscribes the `official-core` pack
 * explicitly via `loadBundledPack(OFFICIAL_CORE_PACK_ID)` / `loadPacks`.
 *
 * Idempotent — each built-in is keyed by its stable id, so re-running is a
 * pure last-writer overwrite.
 */
export function loadCoreInto(registry: PredicateRegistry): void {
  registerBuiltins(registry);
}

// ─── II.D.2 (#57) — multi-pack composition ─────────────────────────

/**
 * The result of `loadPacks()` / `mergePacks()` — the topologically
 * ordered packs plus the pre-populated `PredicateRegistry`. Because
 * predicate ids are opaque + minted (ADR-0004 §1), every declaration
 * keeps its own id in the registry; there is no re-keying.
 */
export interface LoadedPackBundle {
  /** Ordered packs (topological — dependencies first). */
  readonly packs: readonly Pack[];
  /** Registry pre-populated with every predicate, keyed by its opaque id. */
  readonly registry: PredicateRegistry;
  /**
   * Diagnostics from the load. Empty array on a clean load. All
   * diagnostics are **informational, non-blocking** — they never reject
   * a load. Callers may surface them via the auditor UI for
   * disambiguation hints.
   */
  readonly diagnostics: readonly PackLoadDiagnostic[];
}

/**
 * Load diagnostics — all **informational, non-blocking** (ADR-0004 #174).
 *
 * `predicate_name_duplicated` replaces the old
 * `predicate_collision_namespaced` blocking re-key. With opaque ids two
 * predicates can share a display NAME without sharing identity; this
 * advisory records that two distinct ids resolve to the same
 * default-locale name so a UI can disambiguate by pack origin. It never
 * mutates an id and never blocks the load.
 */
export interface PackLoadDiagnostic {
  kind: 'predicate_name_duplicated';
  /** The shared default-locale display name. */
  name: string;
  /** The opaque id of the already-registered declaration. */
  existing_predicate_id: string;
  /** The pack that owns the already-registered declaration. */
  existing_pack_id: string;
  /** The opaque id of the newly-registered, same-named declaration. */
  duplicate_predicate_id: string;
  /** The pack that owns the newly-registered declaration. */
  duplicate_pack_id: string;
}

/**
 * Error thrown when the dependency graph contains a cycle. The cycle
 * is captured as the ordered list of pack ids forming the loop.
 */
export class PackDependencyCycleError extends Error {
  readonly cycle: readonly string[];
  constructor(cycle: readonly string[]) {
    super(`pack dependency cycle: ${cycle.join(' → ')}`);
    this.cycle = cycle;
    this.name = 'PackDependencyCycleError';
  }
}

/**
 * Error thrown when a pack declares a `depends_on` entry that resolves
 * to an unknown pack id.
 */
export class UnknownPackDependencyError extends Error {
  readonly pack_id: string;
  readonly missing_dependency: string;
  constructor(packId: string, missingDependency: string) {
    super(`pack "${packId}" depends on unknown pack "${missingDependency}"`);
    this.pack_id = packId;
    this.missing_dependency = missingDependency;
    this.name = 'UnknownPackDependencyError';
  }
}

/**
 * Parse the `depends_on` field. The manifest schema accepts both bare
 * string ids (`'core'`) and `{ pack, version }` object entries
 * (II.D.1 / #58). Version constraints are advisory at Phase 1.
 */
function dependencyIds(pack: Pack): readonly string[] {
  const raw = pack.manifest.depends_on ?? [];
  const out: string[] = [];
  for (const entry of raw) {
    if (typeof entry === 'string') out.push(entry);
    else if (entry && typeof entry === 'object' && 'pack' in entry && typeof entry.pack === 'string')
      out.push(entry.pack);
  }
  return out;
}

/**
 * Topological sort by dependency. Returns packs in load order
 * (dependencies first). Throws `PackDependencyCycleError` on a cycle,
 * `UnknownPackDependencyError` on a missing dependency.
 *
 * Implementation: depth-first traversal with three-color marking
 * (white = unseen, gray = in progress = on the current path, black =
 * completed). Gray hit indicates a back-edge → cycle.
 */
function topologicalSort(packs: readonly Pack[]): Pack[] {
  const byId = new Map<string, Pack>();
  for (const p of packs) byId.set(p.manifest.id, p);

  const WHITE = 0;
  const GRAY = 1;
  const BLACK = 2;
  const color = new Map<string, number>();
  const stack: string[] = [];
  const out: Pack[] = [];

  const visit = (id: string): void => {
    const c = color.get(id) ?? WHITE;
    if (c === BLACK) return;
    if (c === GRAY) {
      const cycleStart = stack.indexOf(id);
      const cycle = cycleStart >= 0 ? [...stack.slice(cycleStart), id] : [id];
      throw new PackDependencyCycleError(cycle);
    }
    const pack = byId.get(id);
    if (!pack) {
      throw new UnknownPackDependencyError(id, id);
    }
    color.set(id, GRAY);
    stack.push(id);
    for (const dep of dependencyIds(pack)) {
      if (!byId.has(dep)) {
        throw new UnknownPackDependencyError(id, dep);
      }
      visit(dep);
    }
    stack.pop();
    color.set(id, BLACK);
    out.push(pack);
  };

  const orderedIds = [...byId.keys()].sort();
  for (const id of orderedIds) visit(id);
  return out;
}

/**
 * Merge already-loaded packs into a bundle: topologically sort them,
 * register every predicate (each keyed by its opaque id), and emit
 * informational name-duplication advisories.
 *
 * Predicate composition policy (ADR-0004 §1, #174):
 *
 *   - Every predicate keeps its opaque minted id; ids never collide, so
 *     no declaration is ever re-keyed. All predicates from every pack
 *     are present in the merged registry under their own ids.
 *   - When two predicates resolve to the SAME default-locale display
 *     name, a non-blocking `predicate_name_duplicated` advisory records
 *     the pair. This is purely informational — both predicates coexist;
 *     the advisory only helps a UI disambiguate by pack origin.
 *
 * `previous_names` survives as optional name-history (search/recall) but
 * is NOT identity-bearing (ADR-0004 §1 / #172) — the op log is never
 * rewritten and replay stays byte-identical (02-architecture.md §4.2).
 *
 * This is the inline-pack composition primitive `loadPacks` is built on;
 * callers that already hold parsed `Pack`s (e.g. user-authored imports)
 * can use it directly.
 */
export function mergePacks(packs: readonly Pack[]): LoadedPackBundle {
  const sorted = topologicalSort(packs);

  const registry = new PredicateRegistry();
  const diagnostics: PackLoadDiagnostic[] = [];

  // The built-in system predicates (ADR-0004 §2, #173) are present in every
  // campaign regardless of which packs are subscribed — so even a zero-pack
  // bundle resolves entity titles via the built-in `name`.
  registerBuiltins(registry);

  // name → { id, packId } of the first declaration registered under it,
  // for the non-blocking duplicate-name advisory.
  const nameOwner = new Map<string, { id: string; packId: string }>();

  for (const pack of sorted) {
    const packId = pack.manifest.id;
    for (const decl of pack.predicates) {
      registry.add(decl);

      const name = defaultLocaleName(decl.label);
      if (name !== null) {
        const existing = nameOwner.get(name);
        if (existing === undefined) {
          nameOwner.set(name, { id: decl.id, packId });
        } else if (existing.id !== decl.id) {
          diagnostics.push({
            kind: 'predicate_name_duplicated',
            name,
            existing_predicate_id: existing.id,
            existing_pack_id: existing.packId,
            duplicate_predicate_id: decl.id,
            duplicate_pack_id: packId,
          });
        }
      }
    }
  }

  return Object.freeze({
    packs: sorted,
    registry,
    diagnostics,
  });
}

/**
 * Load multiple bundled packs into a merged bundle. Order of `packIds` is a
 * caller hint only — the actual load order is derived from the topological
 * sort of declared dependencies. Transitive dependencies declared via
 * `depends_on` are pulled in automatically (e.g. `generic-medieval-fantasy`
 * brings `official-core`).
 *
 * ADR-0004 §3 (#183): there is no implicit `core` pack any more — the built-in
 * `name` predicate is always registered by `mergePacks` (`registerBuiltins`),
 * so a zero-pack bundle still resolves entity titles. Content vocabulary is
 * only present when its pack is named (or pulled in transitively).
 *
 * See `mergePacks` for the predicate composition + diagnostics policy.
 */
export function loadPacks(packIds: readonly string[]): LoadedPackBundle {
  const idsToLoad = new Set<string>();
  const collect = (id: string): void => {
    if (idsToLoad.has(id)) return;
    idsToLoad.add(id);
    const pack = loadBundledPack(id);
    for (const dep of dependencyIds(pack)) collect(dep);
  };
  for (const id of packIds) collect(id);
  const packs: Pack[] = [];
  for (const id of idsToLoad) packs.push(loadBundledPack(id));
  return mergePacks(packs);
}
